import React, { useContext } from 'react';

import { useFormContext } from 'react-hook-form';

import { useMutation } from '@tanstack/react-query';
import PropTypes from 'prop-types';

import Button from '@asteria/component-core/button';
import Group from '@asteria/component-core/group';

import { useFormValues } from '@asteria/component-form';

import { TranslationService } from '@asteria/language';
import { cn } from '@asteria/utils-funcs/classes';

import { ForecasterContext } from '../../context';
import { useProperties } from '../../hooks';

import { useFormSubmit, useNavigation } from './hooks';

import './styles.scss';

const SectionActions = React.memo((props) => {
	const { variant } = props;
	const { onAction } = useContext(ForecasterContext);

	const { type, category, tag } = useNavigation({ variant });
	const submit = useFormSubmit();

	const { getValues, setValue } = useFormContext();

	const { label } = useProperties({
		type: type,
		category: category,
		tag: tag,
		useForm: useFormValues,
		useStaticForm: getValues,
	});

	const mutation = useMutation({
		mutationFn: async () => {
			await submit?.();
			setValue('$touched', false);

			await onAction?.('action:remove');
			await onAction?.('navigate', null);

			return null;
		},
	});

	return (
		<Group
			className={cn(
				'asteria-component__forecaster-page-section',
				'asteria--type-actions',
			)}
			direction="horizontal"
			verticalAlign="center"
			horizontalAlign="center"
		>
			<Button
				variant="link"
				size="sm"
				label={TranslationService.get(
					[
						'button.back',
						'action.back',
						'forecaster.page.action.back',
						'forecaster.page.category.action.back',
					],
					undefined,
					{ tag: label },
				)}
				onClick={() => onAction?.('navigate', null)}
				disabled={mutation.isLoading}
			/>
			<Button
				variant="primary"
				size="sm"
				label={TranslationService.get(
					[
						'button.submit',
						'action.submit',
						'forecaster.page.action.submit',
						'forecaster.page.category.action.submit',
					],
					undefined,
					{ tag: label },
				)}
				onClick={mutation.mutateAsync}
				loading={mutation.isLoading}
				disabled={mutation.isLoading}
			/>
		</Group>
	);
});

SectionActions.displayName = 'SectionActions';
SectionActions.propTypes = {
	onAction: PropTypes.func,
	onSubmit: PropTypes.func,
	variant: PropTypes.string,
	type: PropTypes.string,
	category: PropTypes.string,
	tag: PropTypes.string,
};

export default SectionActions;
